import bowser from "bowser";
import { useCallback } from "react";
import { resolve } from "url";

import { useLocale } from "@ctv/shared-core/src";

import { useAuth } from "../auth/CognitoAuthContext";
import config from "../config";
import ApiError from "./ApiError";
import {
  ApiOption,
  ApiResponse,
  ApiResult,
  FailedApiResponse,
  ResponseError,
} from "./types";

const defaultHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

export default function useClientApi<Response = unknown, Request = unknown>(
  option: ApiOption
) {
  const { getToken, isAuthenticated } = useAuth();
  const { locale } = useLocale();

  const {
    domain,
    method,
    path,
    withAuth = true,
    localePrefix = true,
    options: paramOptions,
  } = option;
  const { apiHost } = config;

  return useCallback(
    async (
      payload: Request = {} as Request,
      requestInit?: RequestInit
    ): Promise<ApiResult<Response>> => {
      let authServiceToken: string | undefined;
      if (withAuth && isAuthenticated) {
        authServiceToken = await getToken();
      }

      const { headers, ...restOptions } = { ...paramOptions, ...requestInit };
      const options: RequestInit = {
        headers: {
          ...defaultHeaders,
          Origin: window.location.origin,
          ...headers,
        },
        method,
        ...restOptions,
      };

      if (method === "post") {
        // Quick fix to pass `trackingSpec` outside of `data`
        let trackingSpec: unknown = undefined;
        // @ts-expect-error: TS2322 because Type 'Request' is not assignable to type 'object'.
        if ("trackingSpec" in payload) {
          const browser = bowser
            .getParser(window.navigator.userAgent)
            .getBrowser();

          trackingSpec = Object.assign({}, payload.trackingSpec, {
            webUrl: window.location.href,
            webReferrer: null,
            webBrowser: browser.name,
            webBrowserVersion: browser.version,
          });

          delete payload.trackingSpec;
        }

        options.body = JSON.stringify({
          clientInterface: "desktop",
          context: {
            authServiceToken,
          },
          data: payload,
          fields: [],
          trackingSpec,
        });
      }

      const apiUrl = resolve(
        apiHost[domain],
        ((localePrefix ? locale : "") + "/" + path).replace(/\/+/, "/")
      );
      const res = await fetch(apiUrl, options)
        .then<ApiResponse<Response>>((res) => res.json())
        .catch(() => null);

      /**
       * Type guard for 404, play framework html error, and any other error resulting
       * in non-json return type
       */
      if (typeof res !== "object" || res === null) {
        return {
          success: false,
          error: new TypeError("Api call failed"),
        };
      }

      // Public API
      if ("errorType" in res) {
        const { errorType } = res;

        let errorMessage = res.userErrorMessage;
        if (errorType === ResponseError.NOT_AUTHORIZED) {
          errorMessage = res.errorMessage;
        }

        return {
          success: false,
          error: new ApiError(errorType, errorMessage),
        };
      }

      // CTV API
      const failedData = res.data as FailedApiResponse["data"];
      if (
        failedData &&
        "errorMessage" in failedData &&
        failedData.success === false
      ) {
        const { success, errorMessage } = failedData;

        return {
          success,
          error: new ApiError("API_FAILED", errorMessage),
        };
      }

      return {
        success: true,
        // @ts-expect-error: TS2322 because the `?? res` is to cover for the non-standard CTV response like MFA OTP
        data: res.data ?? res,
      };
    },
    [
      domain,
      method,
      path,
      isAuthenticated,
      locale,
      getToken,
      apiHost,
      localePrefix,
      withAuth,
      paramOptions,
    ]
  );
}
